/**
 * The signalling socket: JSON-RPC 2.0 over `wss://<host>/stream/ws`.
 *
 * ## What is different here from every WebRTC tutorial
 *
 * **The DEVICE sends the offer.** A tutorial starts with `createOffer()` on the
 * client. Here the client calls `connect`, the device answers with an `invite`
 * carrying the offer, and the client replies with the answer. Code written the usual
 * way does not work and does not say why (cameras/live-video.md §1.1) — so this module has no
 * `createOffer` in it at all, and `invite` is an INBOUND request this socket must be
 * able to answer, which is the whole reason it speaks both halves of JSON-RPC rather
 * than only making calls.
 *
 * **`authorization` is the bare JWT, never `Bearer <jwt>`.** The prefix is an HTTP
 * header convention; inside a JSON-RPC parameter the string goes straight into
 * signature verification, and a prefixed token comes back `401 token invalid` —
 * indistinguishable from a wrong key. §4.2 records that this has cost time twice.
 *
 * **`register` must be first.** Any other method before it is `401 register first`.
 *
 * ## ⚠️ Wire shapes: what is documented and what is inferred
 *
 * `docs/api/cameras/live-video.md` §4 names the methods (`register`, `unregister`, `connect`,
 * `invite`, `trickle`, `extend`), the subprotocols, the parameter `authorization`, the
 * `connect` parameters (`peer`, `profile`), the `connect` result fields (`session`,
 * `expiryTimeSeconds`, ICE servers) and every error code. It does NOT spell out the
 * field names carrying the SDP in `invite` or the candidate in `trickle` — those come
 * from ONVIF WebRTC 26.06, which this repo does not contain.
 *
 * The response to that is not a guess dressed as a fact. Reading is TOLERANT: each
 * inbound shape is accepted in the spellings a JSON-RPC WebRTC signal plausibly uses,
 * and an unrecognised one raises a named error instead of silently producing an
 * `undefined` SDP that fails four steps later as "connection failed". Writing uses one
 * spelling, marked below. If `sv_stream/README.md` says otherwise, the two
 * `WIRE` constants and `readOffer`/`readCandidate` are the entire surface to correct.
 */

export const SIGNALING_SUBPROTOCOL = 'webrtc.onvif.org';

/** Documented codes, §4.3. Anything else is passed through untouched. */
export const SIGNALING_CODES = {
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  noSession: 404,
  peerTimeout: 408,
  expired: 410,
  offline: 480,
  overloaded: 1001,
} as const;

export class SignalingError extends Error {
  readonly code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'SignalingError';
    this.code = code;
  }
}

export type IceServerInit = {
  urls: string | string[];
  username?: string;
  credential?: string;
};

export type ConnectResult = {
  sessionId: string;
  /** Seconds from now. `extend` before this elapses or the session dies. */
  expiryTimeSeconds: number;
  iceServers: IceServerInit[];
};

export type OfferInit = {sessionId: string; sdp: string};

export type CandidateInit = {
  candidate: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
};

type Json = Record<string, unknown>;

const isObject = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

// ── Inbound readers ─────────────────────────────────────────────────────────────

/**
 * The offer out of an `invite`. Four spellings accepted, in the order a JSON-RPC
 * WebRTC signal is likely to use them; the first that yields a non-empty SDP wins.
 */
export function readOffer(params: unknown): OfferInit {
  if (!isObject(params)) {
    throw new SignalingError(SIGNALING_CODES.badRequest, 'invite carried no params');
  }
  const nested = ['offer', 'description', 'sessionDescription', 'sdpOffer'];
  let sdp = str(params.sdp);
  for (const key of nested) {
    if (sdp) {
      break;
    }
    const inner = params[key];
    sdp = str(inner) ?? (isObject(inner) ? str(inner.sdp) : null);
  }
  if (!sdp) {
    throw new SignalingError(
      SIGNALING_CODES.badRequest,
      'invite carried no SDP in any known field',
    );
  }
  // `session` is the ONVIF field (sv_stream protocol.rs); `sessionId` kept as a
  // fallback for sources written against the old guess.
  return {sessionId: str(params.session) ?? str(params.sessionId) ?? '', sdp};
}

/**
 * Candidates out of a `trickle`. Returns a list because the flat form carries one and
 * the batched form carries many; an end-of-candidates marker (an empty candidate
 * string) yields none, which is correct — there is nothing to add.
 */
function readCandidates(params: unknown): CandidateInit[] {
  if (!isObject(params)) {
    return [];
  }
  const raw: unknown[] = Array.isArray(params.candidates)
    ? params.candidates
    : [params.candidate ?? params];

  const out: CandidateInit[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      if (item) {
        out.push({
          candidate: item,
          sdpMid: str(params.sdpMid),
          sdpMLineIndex:
            typeof params.sdpMLineIndex === 'number' ? params.sdpMLineIndex : null,
        });
      }
      continue;
    }
    if (!isObject(item)) {
      continue;
    }
    const candidate = str(item.candidate);
    if (!candidate) {
      continue;
    }
    out.push({
      candidate,
      sdpMid: str(item.sdpMid),
      sdpMLineIndex:
        typeof item.sdpMLineIndex === 'number' ? item.sdpMLineIndex : null,
    });
  }
  return out;
}

/** ICE servers out of a `connect` result, under any of three plausible keys. */
function readIceServers(result: Json): IceServerInit[] {
  for (const key of ['iceServers', 'servers', 'iceServerList']) {
    const value = result[key];
    if (Array.isArray(value)) {
      return value.filter(isObject).map(s => ({
        urls: (s.urls ?? s.url) as string | string[],
        username: str(s.username) ?? undefined,
        credential: str(s.credential) ?? str(s.password) ?? undefined,
      }));
    }
  }
  return [];
}

// ── Outbound shapes ─────────────────────────────────────────────────────────────

/**
 * The one spelling this client SENDS. `type` rides along with `sdp` so a server
 * reading either field is satisfied by the same message; it is not a second guess.
 */
/** The session id out of a `connect` result — ONVIF names it `session`. */
export function readConnectSession(result: unknown): string {
  const body = isObject(result) ? result : {};
  return str(body.session) ?? str(body.sessionId) ?? str(body.session_id) ?? '';
}

// The shapes sv_stream speaks (sv_stream/src/protocol.rs, ONVIF WebRTC 26.06). The
// first real camera (2026-09-30) showed the earlier guesses — `{type, sdp}` and
// `sessionId` — reaching nobody: the camera found no `answer` and every trickle came
// back `1003 missing field session`.
export const wireAnswer = (sdp: string) => ({answer: sdp});

export const wireExtend = (session: string, authorization: string) => ({
  session,
  authorization,
});

export const wireTrickle = (session: string, c: CandidateInit) => ({
  session,
  candidate: {
    candidate: c.candidate,
    sdpMid: c.sdpMid ?? null,
    sdpMLineIndex: c.sdpMLineIndex ?? null,
  },
});

// ── The socket ──────────────────────────────────────────────────────────────────

export type SignalingHandlers = {
  /** An inbound offer. The returned SDP is sent back as this request's result. */
  onInvite: (offer: OfferInit) => Promise<string>;
  onTrickle: (candidates: CandidateInit[]) => void;
  /**
   * The socket ended. `expected` is true only for a close this client asked for —
   * everything else, including the token reaching its `exp`, arrives WITHOUT a close
   * code (§4.5) and so cannot be told apart from a network drop. The UI must not
   * claim to know which it was.
   */
  onClosed: (expected: boolean) => void;
};

type Pending = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
};

export class SignalingSocket {
  private ws: WebSocket | null = null;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private closing = false;

  constructor(
    private readonly url: string,
    private readonly token: string,
    private readonly handlers: SignalingHandlers,
  ) {}

  /**
   * Open the socket and `register`. Resolves once registration succeeded, because
   * every other method is `401` before that and there is no useful state in between.
   */
  async open(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      // The token rides in a SUBPROTOCOL, not a query parameter: query strings land
      // in proxy logs, which is the same reason the token is short-lived at all.
      const ws = new WebSocket(this.url, [
        SIGNALING_SUBPROTOCOL,
        `Bearer.${this.token}`,
      ]);
      this.ws = ws;

      ws.onopen = () => resolve();
      ws.onmessage = event => this.receive(event.data);
      ws.onerror = () => {
        // `error` on a WebSocket carries nothing usable by design (it would leak
        // cross-origin information), so there is no detail to report here. `onclose`
        // always follows and is where the failure is actually handled.
      };
      ws.onclose = () => {
        const expected = this.closing;
        this.failAll(new SignalingError(SIGNALING_CODES.expired, 'socket closed'));
        this.handlers.onClosed(expected);
        if (!expected) {
          reject(new SignalingError(SIGNALING_CODES.expired, 'socket closed'));
        }
      };
    });

    await this.call('register', {authorization: this.token});
  }

  /** `connect` — asks the box for a session. The offer arrives afterwards, as `invite`. */
  async connect(peer: string, profile: string): Promise<ConnectResult> {
    const result = await this.call('connect', {
      peer,
      // ALWAYS sent, even when the source has one stream: a token's `profiles` claim
      // is the whitelist, and `connect` without a profile is refused outright — else
      // deleting one JSON line would sidestep the whole list (§3.2).
      profile,
      authorization: this.token,
    });
    const body = isObject(result) ? result : {};
    return {
      sessionId: readConnectSession(body),
      expiryTimeSeconds:
        typeof body.expiryTimeSeconds === 'number' ? body.expiryTimeSeconds : 300,
      iceServers: readIceServers(body),
    };
  }

  /**
   * Push one of our candidates. Fire-and-forget: a trickle that fails must not tear
   * down a connection that may well succeed on the candidates that did arrive.
   */
  trickle(sessionId: string, candidate: CandidateInit): void {
    this.notify('trickle', wireTrickle(sessionId, candidate));
  }

  /**
   * Keep the session alive. `authorization` is REQUIRED here — the one place this
   * service knowingly departs from ONVIF, where the field is optional (§4.4). Without
   * it an expired session could be extended with an already-revoked token, and
   * revocation would be a button that does nothing.
   */
  async extend(sessionId: string): Promise<number> {
    const result = await this.call('extend', wireExtend(sessionId, this.token));
    const body = isObject(result) ? result : {};
    return typeof body.expiryTimeSeconds === 'number' ? body.expiryTimeSeconds : 300;
  }

  close(): void {
    this.closing = true;
    try {
      this.ws?.close();
    } catch {
      // Already gone. Nothing to do — onclose has run or will not run.
    }
  }

  // ── plumbing ──────────────────────────────────────────────────────────────────

  private call(method: string, params: unknown): Promise<unknown> {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== 1) {
        reject(new SignalingError(SIGNALING_CODES.expired, 'socket is not open'));
        return;
      }
      this.pending.set(id, {resolve, reject});
      this.ws.send(JSON.stringify({jsonrpc: '2.0', id, method, params}));
    });
  }

  private notify(method: string, params: unknown): void {
    if (this.ws?.readyState === 1) {
      this.ws.send(JSON.stringify({jsonrpc: '2.0', method, params}));
    }
  }

  private send(payload: Json): void {
    if (this.ws?.readyState === 1) {
      this.ws.send(JSON.stringify({jsonrpc: '2.0', ...payload}));
    }
  }

  private receive(data: unknown): void {
    let message: unknown;
    try {
      message = JSON.parse(typeof data === 'string' ? data : String(data));
    } catch {
      return; // Not JSON — nothing this protocol can do with it.
    }
    if (!isObject(message)) {
      return;
    }

    // A response to something we sent.
    if (message.id !== undefined && message.method === undefined) {
      const waiter = this.pending.get(Number(message.id));
      if (!waiter) {
        return;
      }
      this.pending.delete(Number(message.id));
      if (isObject(message.error)) {
        const code =
          typeof message.error.code === 'number'
            ? message.error.code
            : SIGNALING_CODES.badRequest;
        waiter.reject(
          new SignalingError(code, str(message.error.message) ?? 'signalling error'),
        );
      } else {
        waiter.resolve(message.result);
      }
      return;
    }

    // A request FROM the server. `invite` is the one that must be answered.
    if (message.method === 'invite') {
      void this.answerInvite(message);
      return;
    }
    if (message.method === 'trickle') {
      this.handlers.onTrickle(readCandidates(message.params));
      if (message.id !== undefined) {
        this.send({id: message.id, result: {}});
      }
    }
  }

  private async answerInvite(message: Json): Promise<void> {
    try {
      const offer = readOffer(message.params);
      const answer = await this.handlers.onInvite(offer);
      if (message.id !== undefined) {
        this.send({id: message.id, result: wireAnswer(answer)});
      }
    } catch (e) {
      if (message.id !== undefined) {
        const code =
          e instanceof SignalingError ? e.code : SIGNALING_CODES.badRequest;
        this.send({
          id: message.id,
          error: {code, message: e instanceof Error ? e.message : 'answer failed'},
        });
      }
    }
  }

  private failAll(error: Error): void {
    for (const waiter of this.pending.values()) {
      waiter.reject(error);
    }
    this.pending.clear();
  }
}
